import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Users,
  UserPlus,
  BarChart3,
  Zap,
  MessagesSquare,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";

// Static data lives outside the component so it isn't rebuilt on every render
const FEATURES = [
  {
    id: 1,
    icon: Users,
    title: "Omnichannel Lead Capture",
    description:
      "Automatically consolidate leads from multiple sources including web forms, social media, and bulk SMS campaigns into a single unified dashboard.",
    bulletPoints: ["Zero-leakage data integration", "Instant source attribution"],
  },
  {
    id: 2,
    icon: UserPlus,
    title: "Smart Auto-Assignment",
    description:
      "Distribute leads instantly to the right sales representatives based on predefined routing rules, availability, and lead geographic criteria.",
    bulletPoints: ["Round-robin distribution", "Performance-based routing"],
  },
  {
    id: 3,
    icon: Zap,
    title: "Automated Nurturing Workflows",
    description:
      "Engage prospects systematically with automated email and SMS drip campaigns. Move leads down the pipeline without manual intervention.",
    bulletPoints: ["Trigger-based messaging", "Customizable status updates"],
  },
  {
    id: 4,
    icon: MessagesSquare,
    title: "Centralized Communication",
    description:
      "Maintain a complete timeline of every interaction. Access call logs, email threads, and meeting notes attached directly to the lead profile.",
    bulletPoints: ["360-degree prospect view", "One-click follow-up scheduling"],
  },
  {
    id: 5,
    icon: BarChart3,
    title: "Pipeline & ROI Analytics",
    description:
      "Gain real-time visibility into your sales performance. Identify bottlenecks, forecast revenue, and track team conversion metrics effortlessly.",
    bulletPoints: ["Customizable KPI dashboards", "Advanced conversion reports"],
  },
  {
    id: 6,
    icon: ShieldCheck,
    title: "Secure Role-Based Access",
    description:
      "Protect your sensitive customer data with enterprise-grade security. Control exact permissions for managers, executives, and field agents.",
    bulletPoints: ["Granular user permissions", "Comprehensive activity logs"],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 70, damping: 15 },
  },
};

const FeaturesSection = () => {
  const reduced = useReducedMotion();

  // With reduced motion, show everything immediately (no slide / stagger)
  const container = reduced ? undefined : containerVariants;
  const card = reduced ? undefined : cardVariants;

  return (
    // Split layout: a deep-purple header band on top, and a clean light
    // surface below. The first row of cards overlaps the band so the two
    // halves feel connected.
    <section
      id="features"
      aria-labelledby="features-heading"
      className="relative isolate w-full overflow-hidden bg-gradient-to-b from-white to-[#f6f3fc] font-sans text-[#28194f]"
    >
      {/* ===== Header band ===== */}
      <div className="relative overflow-hidden rounded-b-[2rem] bg-gradient-to-r from-[#28194f] via-[#48247f] to-[#702cb0] pb-32 pt-14 text-white sm:rounded-b-[4rem] sm:pb-36 sm:pt-16 lg:pb-40 lg:pt-20">
        <div className="pointer-events-none absolute -left-32 -top-24 h-96 w-96 rounded-full bg-indigo-400/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-20 h-96 w-96 rounded-full bg-fuchsia-400/15 blur-3xl" />

        <motion.div
          initial={reduced ? false : { opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-3xl px-6 text-center"
        >
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium text-[#12dfbd] backdrop-blur-md">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#12dfbd]" />
            Key Features
          </div>

          <h2
            id="features-heading"
            className="text-balance text-[26px] font-light leading-[1.25] tracking-tight sm:text-[34px] lg:text-[38px] xl:text-[42px]"
          >
            Powerful LMS CRM{" "}
            <span className="bg-gradient-to-r from-[#12dfbd] to-[#c9b5ff] bg-clip-text font-normal text-transparent">
              Lead Management
            </span>
          </h2>

          <p className="mx-auto mt-4 max-w-[580px] text-sm font-light leading-[1.8] text-white/90 sm:text-base">
            Accelerate your sales cycle with an intelligent CRM built to
            capture, qualify, and convert leads into loyal clients seamlessly.
          </p>

          <div className="mx-auto mt-6 h-1 w-24 rounded-full bg-gradient-to-r from-[#12dfbd] via-[#c9b5ff] to-[#702cb0]" />
        </motion.div>
      </div>

      {/* ===== Cards (pulled up over the band) ===== */}
      <div className="relative z-10 mx-auto -mt-20 w-full max-w-[1500px] px-4 pb-14 sm:-mt-24 sm:px-10 sm:pb-16 lg:-mt-28 lg:px-14 lg:pb-20 xl:px-20">
        <motion.div
          variants={container}
          initial={reduced ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8"
        >
          {FEATURES.map(({ id, icon: Icon, title, description, bulletPoints }) => (
            <motion.article
              key={id}
              variants={card}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#48247f]/10 bg-white p-6 shadow-[0_8px_30px_-12px_rgba(72,36,127,0.25)] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#702cb0]/40 hover:shadow-[0_24px_48px_-12px_rgba(72,36,127,0.4)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:p-8"
            >
              {/* Top accent line: reveals on hover */}
              <div className="absolute left-0 top-0 h-1 w-full origin-left scale-x-0 bg-gradient-to-r from-[#12dfbd] via-[#702cb0] to-[#48247f] transition-transform duration-500 ease-out group-hover:scale-x-100" />

              {/* Icon + index number */}
              <div className="mb-6 flex items-start justify-between">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#f1ecfa] text-[#48247f] transition-all duration-300 group-hover:-rotate-6 group-hover:bg-gradient-to-br group-hover:from-[#48247f] group-hover:to-[#702cb0] group-hover:text-white">
                  <Icon className="h-6 w-6" />
                </div>
                <span className="text-3xl font-light leading-none text-[#48247f]/15 transition-colors duration-300 group-hover:text-[#12dfbd]">
                  {String(id).padStart(2, "0")}
                </span>
              </div>

              <h3 className="mb-3 text-lg font-medium tracking-tight text-[#28194f] sm:text-xl">
                {title}
              </h3>

              <p className="mb-6 flex-grow text-sm font-normal leading-[1.75] text-slate-700">
                {description}
              </p>

              <ul className="mt-auto space-y-3 border-t border-[#48247f]/10 pt-5">
                {bulletPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <CheckCircle2
                      className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#0f9d86]"
                      strokeWidth={2.5}
                    />
                    <span className="text-sm font-medium text-[#28194f]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;